import api from './api';

// The profile form holds its submit button until this answers, so a server
// that accepts the request and then stalls would lock the person out of
// creating a profile. On a timeout the form proceeds as for any other
// failure: no question, a student profile. The ceiling sits above the
// server's own 5s Graph timeout so a slow but valid answer still arrives.
export const FACULTY_ELIGIBILITY_TIMEOUT_MS = 8000;

export const userService = {
    /**
     * Get current user's profile
     */
    async getProfile() {
        const response = await api.get('/user/profile');
        return response.data;
    },

    /**
     * Create a new user profile
     */
    async createProfile(profileData) {
        const response = await api.post('/user/profile', profileData);
        return response.data;
    },

    /**
     * Whether the new profile will have faculty access. A hint for the form
     * only: the server verifies again when the profile is created.
     * @param {string} microsoftAccessToken - From the Microsoft sign-in
     * @returns {Promise<{ eligible: boolean, jobTitle?: string,
     *   department?: string }>} A display label and the directory department
     *   from the verified directory entry, only when eligible
     */
    async getFacultyEligibility(microsoftAccessToken) {
        const response = await api.post(
            '/user/profile/faculty-eligibility',
            { microsoftAccessToken },
            { timeout: FACULTY_ELIGIBILITY_TIMEOUT_MS }
        );
        return response.data;
    },

    /**
     * Update user profile
     */
    async updateProfile(profileData) {
        const response = await api.put('/user/profile', profileData);
        return response.data;
    },

    /**
     * Upload or replace the signed-in student's profile resume. Sent as
     * multipart; the shared client defaults to JSON, so the content type is
     * replaced and axios supplies the boundary.
     * @param {File} file
     * @returns {Promise<{ resume: { fileName: string, size: number,
     *   uploadedAt: string } }>}
     */
    async uploadResume(file) {
        const form = new FormData();
        form.append('resume', file);
        const response = await api.put('/user/profile/resume', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    /** Remove the resume from the profile. Submitted applications keep theirs. */
    async removeResume() {
        await api.delete('/user/profile/resume');
    },

    /**
     * The profile resume file as a blob. It needs the signed-in user's token,
     * which a plain link cannot send.
     * @returns {Promise<Blob>}
     */
    async downloadResume() {
        const response = await api.get('/user/profile/resume', {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Upload or replace the signed-in user's own profile photo (multipart).
     * @param {File} file
     * @returns {Promise<{ photo: { size: number, uploadedAt: string } }>}
     */
    async uploadPhoto(file) {
        const form = new FormData();
        form.append('photo', file);
        const response = await api.put('/user/profile/photo', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    /** Remove the profile photo. */
    async removePhoto() {
        await api.delete('/user/profile/photo');
    },

    /**
     * The profile photo as a blob. It needs the signed-in user's token, which
     * an image tag cannot send.
     * @returns {Promise<Blob>}
     */
    async downloadPhoto() {
        const response = await api.get('/user/profile/photo', {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Get user by ID (admin function)
     */
    async getUserById(userId) {
        const response = await api.get(`/user/${userId}`);
        return response.data;
    },

    /**
     * Get all users (admin function)
     */
    async getAllUsers(params = {}) {
        const response = await api.get('/user', { params });
        return response.data;
    },

    /**
     * Update a user by ID (admin function)
     * Allows updating name, studentId, department, phone, isActive, userType.
     */
    async updateUser(userId, updates) {
        const response = await api.put(`/user/${userId}`, updates);
        return response.data;
    },

    /**
     * Update a user's roles (admin function)
     * @param {string} userId
     * @param {string[]} roles - Full roles array to set
     */
    async updateUserRoles(userId, roles) {
        const response = await api.put(`/user/${userId}/roles`, { roles });
        return response.data;
    },

    /**
     * Mark the onboarding tour as complete
     */
    async markTourComplete() {
        const response = await api.put('/user/me/tour-status', {
            complete: true,
        });
        return response.data;
    },

    /**
     * Save the current tour step so it can resume later
     */
    async saveTourStep(step) {
        const response = await api.put('/user/me/tour-status', {
            tourStep: step,
        });
        return response.data;
    },

    /**
     * Reset the tour so it shows again on next login
     */
    async resetTour() {
        const response = await api.put('/user/me/tour-status', { reset: true });
        return response.data;
    },
};

export default userService;
